import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, CircleAlert, MessageSquareWarning, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { ErrorState } from '@/components/ui/states';
import { apiError, isSystemFault, type CopilotAnswer } from './lib';

const MAX_QUESTION_LENGTH = 500;

/** A handful of the question shapes the deterministic stub actually maps to tool calls — see ADR-0032 §2.2. */
const EXAMPLE_KEYS = [
  'revenue7d',
  'branchSummary',
  'refusalPayroll',
] as const;

/**
 * §25 / ADR-0032 — the operations copilot.
 *
 * ONE question at a time, not a chat: the backend keeps no conversation
 * history (`conversationId` only correlates one question's own tool calls),
 * so this screen does not pretend otherwise with a scrolling transcript —
 * asking again replaces the answer below rather than appending to a thread.
 *
 * `copilot.use` gates the route (`Routes.tsx`) and is also all this screen
 * needs to check: what the answer may actually cite is decided per-call,
 * server-side, by `reports.view`/`.view_cost` etc. through the same
 * semantic engine the report builder runs under — holding `copilot.use`
 * grants no extra reach, so there is no second permission for a button here
 * to gate on.
 *
 * `dir="auto"` on the question and the answer text: the deterministic stub
 * (and, later, any real model behind the port) composes its sentence in
 * English regardless of the UI locale, and a typed question can be either
 * language — forcing either onto the page's own RTL/LTR direction would
 * scramble the numbers and punctuation embedded in it, so each block picks
 * its own direction off its own first strong character instead.
 */
export default function Copilot() {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<CopilotAnswer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const ask = async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed || asking) return;
    setAsking(true);
    setLoadError(null);
    try {
      const { data } = await api.post<CopilotAnswer>('/copilot/ask', { question: trimmed });
      setAnswer(data);
    } catch (e) {
      setAnswer(null);
      setLoadError(apiError(e, t('copilot.askFailed')));
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Icon as={Sparkles} className="text-accent" />
          {t('copilot.title')}
        </h1>
        <p className="text-sm text-ink-muted">{t('copilot.subtitle')}</p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <Field label={t('copilot.questionLabel')} hint={t('copilot.questionHint')}>
            <Textarea
              rows={3}
              maxLength={MAX_QUESTION_LENGTH}
              dir="auto"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('copilot.questionPlaceholder')}
            />
          </Field>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  dir="auto"
                  onClick={() => setQuestion(t(`copilot.examples.${key}`))}
                  className="rounded-full border px-2.5 py-1 text-xs text-ink-muted hover:bg-hover"
                >
                  {t(`copilot.examples.${key}`)}
                </button>
              ))}
            </div>
            <Button onClick={() => void ask(question)} disabled={asking || !question.trim()}>
              {asking ? t('copilot.asking') : t('copilot.ask')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loadError && <ErrorState message={loadError} onRetry={() => void ask(question)} />}

      {answer && <AnswerCard answer={answer} onRetry={() => void ask(question)} />}
    </div>
  );
}

function AnswerCard({ answer, onRetry }: { answer: CopilotAnswer; onRetry: () => void }) {
  const { t } = useTranslation();
  const [traceOpen, setTraceOpen] = useState(false);

  // A system fault (a governed read that threw mid-call) is NOT the same
  // screen as a governed refusal — the empty-vs-broken distinction, applied
  // to an answer rather than a list. See `isSystemFault`'s own doc comment.
  if (answer.refused && isSystemFault(answer)) {
    return <ErrorState message={answer.text} onRetry={onRetry} />;
  }

  if (answer.refused) {
    return (
      <Card className="border-line-strong">
        <CardContent className="flex items-start gap-3 p-4">
          <Icon as={MessageSquareWarning} className="mt-0.5 shrink-0 text-ink-muted" />
          <div>
            <p className="text-sm font-medium">{t('copilot.declined')}</p>
            <p dir="auto" className="mt-1 text-sm text-ink-muted">
              {answer.text}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle dir="auto" className="text-base font-normal">
          {answer.text}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {answer.citedMetrics.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted rtl:tracking-normal">
              {t('copilot.citedMetrics')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {answer.citedMetrics.map((metric) => (
                <Badge key={metric} tone="brand" dir="ltr">
                  {metric}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {answer.scope && (
          <p className="text-xs text-ink-muted">
            {answer.scope.branchIds
              ? t('copilot.scopeBranches', { count: answer.scope.branchIds.length })
              : t('copilot.scopeAllBranches')}
          </p>
        )}

        <div>
          <button
            type="button"
            onClick={() => setTraceOpen((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
          >
            <Icon as={ChevronDown} size="xs" className={traceOpen ? 'rotate-180' : undefined} />
            {t('copilot.howAnswered')}
          </button>
          {traceOpen && (
            <ul className="mt-2 space-y-2">
              {answer.toolCalls.map((entry, i) => (
                <li key={i} className="rounded-lg border p-2.5 text-xs">
                  <p className="font-mono font-medium" dir="ltr">
                    {entry.call.tool}
                  </p>
                  {Object.keys(entry.call.arguments).length > 0 && (
                    <p className="mt-0.5 break-all font-mono text-ink-muted" dir="ltr">
                      {JSON.stringify(entry.call.arguments)}
                    </p>
                  )}
                  <p className="mt-1 flex items-center gap-1.5">
                    <Badge
                      tone={
                        entry.outcome.status === 'ok'
                          ? 'success'
                          : entry.outcome.status === 'refused'
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      {t(`copilot.outcome.${entry.outcome.status}`)}
                    </Badge>
                    {entry.outcome.status !== 'ok' && (
                      <span dir="auto" className="flex items-center gap-1 text-ink-muted">
                        <Icon as={CircleAlert} size="xs" />
                        {entry.outcome.reason}
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
